"use client";

import { useState, useRef, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Progress,
  Alert,
  AlertIcon,
  Box,
  Flex,
  Badge,
  Divider,
  List,
  ListItem,
  ListIcon,
  Input,
  FormLabel,
  UnorderedList,
} from "@chakra-ui/react";
import { CheckCircleIcon, WarningIcon, DownloadIcon } from "@chakra-ui/icons";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { BsUpload } from "react-icons/bs";
import { recruitConstant } from "services/Recruit/constant";
import { globalStyles } from "common/theme/styles";
import { constant } from "common/constant";
import { applicationSourceOptions } from "services/Recruit/data/dropdownData";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserData } from "common/redux/reducers/userSlice";
import { qualificationOptions } from "services/Recruit/data/dropdownData";
import { requiredSkillsOptions } from "services/Recruit/data/dropdownData";
import { noticePeriodOptions } from "services/Recruit/data/dropdownData";
import { jobTitleOptions } from "services/Recruit/data/dropdownData";
import { experienceRangeOptions } from "services/Recruit/data/dropdownData";
import { canddiateStatusLabels } from "common/constant";

const formatReasonCount = (count, reason) => {
  const normalizedReason = typeof reason === "string" ? reason.trim() : "";

  if (!normalizedReason) {
    return `${count} record${count === 1 ? "" : "s"} had unknown issues`;
  }

  return `${count} record${count === 1 ? "" : "s"}: ${normalizedReason}`;
};

const buildReasonSentence = (title, reasons = []) => {
  if (!reasons.length) {
    return "";
  }

  const formattedReasons = reasons
    .map((item) => formatReasonCount(item.count, item.reason))
    .join(", ");

  return `${title}: ${formattedReasons}.`;
};

const buildImportToastMessage = (result) => {
  const summary = `${result?.created || 0} created, ${result?.skipped || 0} skipped, ${result?.failed || 0} failed.`;
  const skippedReasons = buildReasonSentence(
    "Skipped reasons",
    result?.groupedReasons?.skipped || []
  );
  const failedReasons = buildReasonSentence(
    "Failed reasons",
    result?.groupedReasons?.failed || []
  );

  return [summary, skippedReasons, failedReasons].filter(Boolean).join(" ");
};

const INVALID_FORMAT_MESSAGE = "Please upload in the required format";

const candidateHeaderAliases = {
  name: ["Name", "Full Name"],
  email: ["Email"],
  phone: ["Phone", "Phone Number"],
  city: ["City"],
  state: ["State"],
  experienceRange: [
    "Experience Range",
    "Experience Years",
    "Experience",
    "Total Experience",
  ],
  highestQualification: ["Highest Qualification", "Qualification"],
  currentJobTitle: ["Current Job Title", "Job Title"],
  currentEmployer: ["Current Employer", "Employer"],
  skills: ["Skills"],
  status: ["Status"],
  noticePeriod: ["Notice Period"],
  source: ["Source"],
  owner: ["Owner"],
};

const requiredCandidateFields = [
  "name",
  "email",
  "phone",
  "experienceRange",
  "highestQualification",
  "currentEmployer",
  "skills",
  "status",
  "source",
  "owner",
];

const normalizeHeaderValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getCandidateFieldName = (header) => {
  const normalizedHeader = normalizeHeaderValue(header);

  const matchedField = Object.entries(candidateHeaderAliases).find(
    ([, aliases]) =>
      aliases.some(
        (alias) => normalizeHeaderValue(alias) === normalizedHeader
      )
  );

  if (matchedField) {
    return matchedField[0];
  }

  return String(header || "").toLowerCase().replace(/\s+/g, "");
};

const hasRequiredCandidateColumns = (headers = []) => {
  const detectedFields = headers.map(getCandidateFieldName);

  return requiredCandidateFields.every((field) =>
    detectedFields.includes(field)
  );
};

const BulkImportModal = ({ isOpen, onClose, onImport }) => {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [step, setStep] = useState("upload"); // "upload" | "processing" | "results"
  const fileInputRef = useRef(null);
  const colors = globalStyles.colors;
  const { users } = useSelector((state) => state.users);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchUserData());
  }, [dispatch]);

  const mapExperienceYearsToRange = (years) => {
    if (!Number.isFinite(years) || years < 0) return "";
    if (years <= 1) return "0-1";
    if (years <= 3) return "1-3";
    if (years <= 5) return "3-5";
    if (years <= 8) return "5-8";
    if (years <= 12) return "8-12";
    return "12+";
  };

  const normalizeExperienceRange = (value) => {
    if (value === null || value === undefined || value === "") {
      return "";
    }

    if (typeof value === "number") {
      return mapExperienceYearsToRange(value);
    }

    const rawValue = String(value).trim();
    if (!rawValue) {
      return "";
    }

    if (/^\d+(\.\d+)?$/.test(rawValue)) {
      return mapExperienceYearsToRange(Number.parseFloat(rawValue));
    }

    const normalizeText = (text) =>
      text
        .toLowerCase()
        .replace(/[–—]/g, "-")
        .replace(/\byrs?\b/g, "year")
        .replace(/\byears\b/g, "year")
        .replace(/\s+/g, "");

    const normalizedValue = normalizeText(rawValue);

    const matchedOption = experienceRangeOptions.find((option) => {
      const normalizedOptionValue = normalizeText(option.value);
      const normalizedOptionLabel = normalizeText(option.label);

      return (
        normalizedValue === normalizedOptionValue ||
        normalizedValue === normalizedOptionLabel ||
        normalizedValue === normalizedOptionLabel.replace("year", "") ||
        normalizedValue === `${normalizedOptionValue}year`
      );
    });

    return matchedOption?.value || "";
  };

  const findUserIdByName = (ownerName) => {
    if (!ownerName || !users || users.length === 0) {
      return null;
    }

    const cleanOwnerName = ownerName.toString().trim();

    const exactMatch = users.find((user) => {
      const userFullName = `${user.firstName || ""} ${
        user.lastName || ""
      }`.trim();
      return (
        userFullName.toLowerCase() === cleanOwnerName.toLowerCase() &&
        user.roles.find((role) => role.roleName === "recruiter")
      );
    });

    if (exactMatch) {
      return exactMatch._id;
    }

    const firstNameMatch = users.find((user) => {
      return (
        user.firstName &&
        user.firstName.toLowerCase() === cleanOwnerName.toLowerCase() &&
        user.roles.find((role) => role.roleName === "recruiter")
      );
    });

    if (firstNameMatch) {
      return firstNameMatch._id;
    }

    // Try to find partial match (if owner name contains first name)
    const partialMatch = users.find((user) => {
      const userFullName = `${user.firstName || ""} ${
        user.lastName || ""
      }`.trim();
      return (
        (userFullName.toLowerCase().includes(cleanOwnerName.toLowerCase()) ||
          cleanOwnerName.toLowerCase().includes(userFullName.toLowerCase())) &&
        user.roles.find((role) => role.roleName === "recruiter")
      );
    });

    if (partialMatch) {
      return partialMatch._id;
    }

    return null; // No match found
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      // Some browsers/OS combinations leave Excel MIME types blank or inconsistent,
      // so validate by MIME type first and fall back to the file extension.
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
      ];
      const validExtensions = [".xlsx", ".xls", ".csv"];
      const fileName = selectedFile.name?.toLowerCase() || "";
      const hasValidExtension = validExtensions.some((extension) =>
        fileName.endsWith(extension)
      );
      const hasValidMimeType = validTypes.includes(selectedFile.type);

      if (!hasValidMimeType && !hasValidExtension) {
        toast.error(
          "Please select a valid Excel file (.xlsx, .xls) or CSV file"
        );
        event.target.value = "";
        return;
      }

      // Validate file size (max 5MB)
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast.error("File size should not exceed 5MB");
        event.target.value = "";
        return;
      }

      parseExcelFile(selectedFile)
        .then(() => {
          setFile(selectedFile);
        })
        .catch(() => {
          setFile(null);
          event.target.value = "";
          toast.error(INVALID_FORMAT_MESSAGE);
        });
    }
  };

  const parseExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          // Skip empty rows and get headers
          const nonEmptyRows = jsonData.filter((row) =>
            row.some((cell) => cell !== undefined && cell !== "")
          );

          if (nonEmptyRows.length < 2) {
            reject(
              new Error(
                "Excel file must contain at least a header row and one data row"
              )
            );
            return;
          }

          const headers = Array.isArray(nonEmptyRows[0]) ? nonEmptyRows[0] : [];

          if (!hasRequiredCandidateColumns(headers)) {
            reject(new Error(INVALID_FORMAT_MESSAGE));
            return;
          }

          const dataRows = nonEmptyRows.slice(1);

          // Convert to objects
          const candidates = dataRows.map((row, index) => {
            const candidate = {};
            headers.forEach((header, headerIndex) => {
              const value = row[headerIndex];
              if (value !== undefined && value !== "") {
                const fieldName = getCandidateFieldName(header);

                // Special handling for skills (convert comma-separated string to array)
                if (fieldName === "skills" && typeof value === "string") {
                  candidate[fieldName] = value
                    .split(",")
                    .map((skill) => skill.trim())
                    .filter(Boolean);
                } else if (fieldName === "experienceRange") {
                  candidate[fieldName] = normalizeExperienceRange(value);
                } else if (fieldName === "owner") {
                  // Find user ID by name
                  const userId = findUserIdByName(value);
                  if (userId) {
                    candidate[fieldName] = userId;
                  } else {
                    // Log warning for unmatched owner names
                    console.warn(
                      `Owner "${value}" not found in users list for row ${
                        index + 2
                      }`
                    );
                    candidate[fieldName] = ""; // Set empty if no match found
                  }
                } else {
                  candidate[fieldName] = value;
                }
              }
            });

            // Add row number for error reporting
            candidate._rowNumber = index + 2; // +2 because we start from row 1 and skip header
            return candidate;
          });

          resolve(candidates);
        } catch (error) {
          reject(
            new Error(
              "Failed to parse Excel file. Please check the file format."
            )
          );
        }
      };

      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsArrayBuffer(file);
    });
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setIsProcessing(true);
    setStep("processing");
    setProgress(10);

    try {
      // Parse Excel file
      setProgress(30);
      const candidatesData = await parseExcelFile(file);

      if (candidatesData.length === 0) {
        toast.error("No valid data found in the file");
        setIsProcessing(false);
        setStep("upload");
        return;
      }

      setProgress(50);

      // Call the import API
      const result = await onImport(candidatesData);

      setProgress(100);
      setResult(result);
      setStep("results");
      if (result.success) {
        toast.success(`Import completed. ${buildImportToastMessage(result)}`);
      } else {
        toast.warning(
          `Import completed with issues. ${buildImportToastMessage(result)}`
        );
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error instanceof Error ? error.message : "Import failed");
      setStep("upload");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setStep("upload");
    setProgress(0);
    setIsProcessing(false);
    onClose();
  };

  const downloadSampleFile = () => {
    const sampleRecruiterNames = users
      ?.filter((user) =>
        user.roles.find((role) => role.roleName === "recruiter")
      )
      ?.slice(0, 2)
      ?.map((user) =>
        `${user.firstName || ""} ${user.lastName || ""}`.trim()
      ) || ["Raman", "STEVEN Hawkins"];
    // Create sample data
    const sampleData = [
      {
        Name: "John Doe",
        Email: "john.doe@example.com",
        Phone: "9876543210",
        City: "Mumbai",
        State: "Maharashtra",
        "Experience Range": "3-5",
        "Highest Qualification": "Higher Secondary (12th)",
        "Current Job Title": "software-engineer",
        "Current Employer": "Tech Corp",
        Skills: "javascript,react,nodejs",
        Status: "Not Assigned",
        "Notice Period": "30_days",
        Source: "Linkedin",
        Owner: sampleRecruiterNames[0] || "Raman",
      },
      {
        Name: "Jane Smith",
        Email: "jane.smith@example.com",
        Phone: "9876543211",
        City: "Bangalore",
        State: "Karnataka",
        "Experience Range": "1-3",
        "Highest Qualification": "Ph.D / Doctorate",
        "Current Job Title": "frontend-developer",
        "Current Employer": "Design Studio",
        Skills: "html,css,javascript,vue",
        Status: "Joined",
        "Notice Period": "60_days",
        Source: "Hirest",
        Owner: sampleRecruiterNames[1] || "STEVEN Hawkins",
      },
    ];

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Candidates");

    // Download file
    XLSX.writeFile(wb, "candidate_import_sample.xlsx");
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{constant.bulkImportCandidate}</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {step === "upload" && (
            <VStack borderRadius={"8px"} spacing={2} align="stretch">
              <Alert status="info">
                {/* <AlertIcon /> */}
                <Box maxHeight={"300px"} overflowY="auto">
                  <Text fontWeight="bold">{constant.importGuidelines}</Text>
                  <Text fontSize={"sm"} mt={4}>
                    • {constant.uploadExcelorCsv}
                  </Text>
                  <Text mt={2} fontSize={"sm"}>
                    • {constant.maximumFileSize5}
                  </Text>
                  <Text mt={2} fontSize={"sm"}>
                    {" "}
                    • {constant.makeSureAllrequiredFieldsAreIncluded}
                  </Text>
                  <Text mt={2} fontSize="sm">
                    • {constant.sourceFieldRule}
                  </Text>
                  <UnorderedList mt={2} fontSize="sm" pl={4}>
                    {applicationSourceOptions?.map((source, idx) => (
                      <ListItem key={idx}>{source?.label}</ListItem>
                    ))}
                  </UnorderedList>

                  <Text fontSize="sm" mt={2}>
                    • {constant.statusFieldRule}
                  </Text>
                  <UnorderedList mt={2} fontSize="sm" pl={4}>
                    {canddiateStatusLabels?.map((status, idx) => (
                      <ListItem key={idx}>{status}</ListItem>
                    ))}
                  </UnorderedList>

                  <Text fontSize="sm" mt={2}>
                    • Owner field value must be one of the following (otherwise
                    it will be left blank):
                  </Text>
                  <UnorderedList mt={2} fontSize="sm" pl={4}>
                    {users?.length > 0 &&
                      users.map((user) =>
                        user?.roles?.find(
                          (role) => role.roleName === "recruiter"
                        ) ? (
                          <ListItem key={user._id}>
                            {(user.firstName || "") +
                              " " +
                              (user.lastName || "")}
                          </ListItem>
                        ) : null
                      )}
                  </UnorderedList>

                  <Text fontSize="sm" mt={2}>
                    • Highest Qualification field value must be one of the
                    following (otherwise it will be left blank):
                  </Text>
                  <UnorderedList mt={2} fontSize="sm" pl={4}>
                    {qualificationOptions?.map((status, idx) => (
                      <ListItem key={idx}>{status?.label}</ListItem>
                    ))}
                  </UnorderedList>

                  <Text fontSize="sm" mt={2}>
                    • Skills field values must be among the following options
                    (any invalid entries will be ignored):
                  </Text>
                  <UnorderedList mt={2} fontSize="sm" pl={4}>
                    {requiredSkillsOptions?.map((status, idx) => (
                      <ListItem key={idx}>{status?.label}</ListItem>
                    ))}
                  </UnorderedList>

                  <Text fontSize="sm" mt={2}>
                    • Notice Period field value must be one of the following
                    (otherwise it will be left blank):
                  </Text>
                  <UnorderedList mt={2} fontSize="sm" pl={4}>
                    {noticePeriodOptions?.map((status, idx) => (
                      <ListItem key={idx}>{status?.label}</ListItem>
                    ))}
                  </UnorderedList>

                  <Text fontSize="sm" mt={2}>
                    • Job Title field value must be one of the following
                    (otherwise it will be left blank):
                  </Text>
                  <UnorderedList mt={2} fontSize="sm" pl={4}>
                    {jobTitleOptions?.map((status, idx) => (
                      <ListItem key={idx}>{status?.label}</ListItem>
                    ))}
                  </UnorderedList>
                </Box>
              </Alert>

              <Button
                py={5}
                leftIcon={<DownloadIcon />}
                variant="outline"
                onClick={downloadSampleFile}
                size="sm"
              >
                Download Sample Excel Template
              </Button>

              <Divider />

              <Box>
                <FormLabel>Select Excel File</FormLabel>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  display="none"
                />
                <Button
                  py={5}
                  leftIcon={<BsUpload />}
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  width="100%"
                >
                  {file ? file.name : "Choose File"}
                </Button>
              </Box>

              {file && (
                <Alert status="success">
                  <AlertIcon />
                  <Text>
                    File selected: {file.name} ({(file.size / 1024).toFixed(1)}{" "}
                    KB)
                  </Text>
                </Alert>
              )}
            </VStack>
          )}

          {step === "processing" && (
            <VStack spacing={4} align="stretch">
              <Text textAlign="center" fontSize="lg" fontWeight="bold">
                Processing Import...
              </Text>
              <Progress value={progress} colorScheme="blue" size="lg" />
              <Text textAlign="center" color="gray.600">
                {progress < 30
                  ? "Reading file..."
                  : progress < 50
                  ? "Parsing data..."
                  : progress < 100
                  ? "Creating candidates..."
                  : "Completing..."}
              </Text>
            </VStack>
          )}

          {step === "results" && result && (
            <VStack spacing={4} align="stretch">
              <Alert status={result.failed === 0 ? "success" : "warning"}>
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold">Import Summary</Text>
                  <Flex gap={4} mt={2}>
                    <Badge colorScheme="green" p={2}>
                      ✓ {result.created} Created
                    </Badge>
                    {result.failed > 0 && (
                      <Badge colorScheme="red" p={2}>
                        ✗ {result.failed} Failed
                      </Badge>
                    )}
                    {result.skipped > 0 && (
                      <Badge colorScheme="orange" p={2}>
                        {result.skipped} Skipped
                      </Badge>
                    )}
                  </Flex>
                </Box>
              </Alert>

              {(result.groupedReasons?.failed?.length > 0 ||
                result.groupedReasons?.skipped?.length > 0) && (
                <Box>
                  <Text fontWeight="bold" mb={2}>
                    Issue Breakdown
                  </Text>
                  <Box
                    border="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    p={3}
                    bg="gray.50"
                  >
                    {result.groupedReasons?.skipped?.length > 0 && (
                      <Box mb={result.groupedReasons?.failed?.length > 0 ? 3 : 0}>
                        <Text fontSize="sm" fontWeight="semibold" color="orange.600" mb={1}>
                          Skipped reasons
                        </Text>
                        <List spacing={1}>
                          {result.groupedReasons.skipped.map((item, index) => (
                            <ListItem key={`skipped-reason-${index}`} fontSize="sm">
                              <ListIcon as={WarningIcon} color="orange.400" />
                              {formatReasonCount(item.count, item.reason)}
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {result.groupedReasons?.failed?.length > 0 && (
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold" color="red.600" mb={1}>
                          Failed reasons
                        </Text>
                        <List spacing={1}>
                          {result.groupedReasons.failed.map((item, index) => (
                            <ListItem key={`failed-reason-${index}`} fontSize="sm">
                              <ListIcon as={WarningIcon} color="red.500" />
                              {formatReasonCount(item.count, item.reason)}
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {result.successfulRecords &&
                result.successfulRecords.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" color="green.600" mb={2}>
                      Successfully Created ({result.created}):
                    </Text>
                    <Box
                      maxH="150px"
                      overflowY="auto"
                      border="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      p={2}
                    >
                      <List spacing={1}>
                        {result.successfulRecords
                          .slice(0, 10)
                          .map((record, index) => (
                            <ListItem key={index} fontSize="sm">
                              <ListIcon
                                as={CheckCircleIcon}
                                color="green.500"
                              />
                              {record.name} ({record.email})
                            </ListItem>
                          ))}
                        {result.successfulRecords.length > 10 && (
                          <ListItem fontSize="sm" color="gray.600">
                            ... and {result.successfulRecords.length - 10} more
                          </ListItem>
                        )}
                      </List>
                    </Box>
                  </Box>
                )}

              {/* {result.errors && result.errors.length > 0 && (
                <Box>
                  <Text fontWeight="bold" color="red.600" mb={2}>
                    Failed Records ({result.failed}):
                  </Text>
                  <Box
                    maxH="200px"
                    overflowY="auto"
                    border="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    p={2}
                  >
                    <List spacing={2}>
                      {result.errors.map((error, index) => (
                        <ListItem key={index} fontSize="sm">
                          <ListIcon as={WarningIcon} color="red.500" />
                          <Text as="span" fontWeight="bold">
                            Row {error.row}:
                          </Text>{" "}
                          {error.name} ({error.email})
                          <Text color="red.600" fontSize="xs" ml={6}>
                            Error: {error.error}
                          </Text>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Box>
              )} */}

            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          {step === "upload" && (
            <>
              <Button
                className="theme-btn2 cancelBtn"
                sx={{
                  textTransform: "capitalize",
                }}
                variant="outline"
                colorScheme={colors.error["100"]}
                size="sm"
                mr={3}
                onClick={handleClose}
              >
                {recruitConstant.cancel}
              </Button>
              <Button
                className="nextBtn"
                variant="primary"
                onClick={handleImport}
                isDisabled={!file || isProcessing}
                isLoading={isProcessing}
              >
                Import Candidates
              </Button>
            </>
          )}

          {step === "processing" && (
            <Button
              colorScheme="red"
              variant="outline"
              onClick={handleClose}
              isDisabled={isProcessing}
            >
              Cancel
            </Button>
          )}

          {step === "results" && (
            <Button className="nextBtn" variant="primary" onClick={handleClose}>
              Close
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BulkImportModal;
